// @ts-nocheck
export function mountHeroPipeline(
  svg: SVGSVGElement,
  reducedMotion: boolean,
  logoSrc = '/ct-logo.webp',
) {
  // ---------- projection ----------
  const S = 0.8, C = Math.cos(Math.PI / 6), CX = 330, CY = 312;
  const iso = (x, y, z = 0) => [CX + (x - y) * C * S, CY + (x + y) * 0.5 * S - z * S];
  const PT = pts => pts.map(p => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
  const plane = (x, y, z) => {
    const [e, f] = iso(x, y, z);
    return `matrix(${(C*S).toFixed(4)},${(0.5*S).toFixed(4)},${(-C*S).toFixed(4)},${(0.5*S).toFixed(4)},${e.toFixed(2)},${f.toFixed(2)})`;
  };
  // Upright logo on a horizontal surface: reads left-to-right on screen, squashed by the iso viewing angle.
  const upright = (cx, cy, z) => {
    const [ex, ey] = iso(cx, cy, z), a = C * S * Math.SQRT2, d = S / Math.SQRT2;
    return `matrix(${a.toFixed(4)},0,0,${d.toFixed(4)},${(ex - 50 * a).toFixed(2)},${(ey - 50 * d).toFixed(2)})`;
  };
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const smooth = t => { t = clamp(t); return t * t * (3 - 2 * t); };
  const easeIO = t => 0.5 - 0.5 * Math.cos(Math.PI * clamp(t));
  const mix = (a, b, t) => a + (b - a) * t;
  const hex = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
  const mixC = (a, b, t, k = 1) => { const A = hex(a), B = hex(b); return `rgb(${A.map((v, i) => Math.round(mix(v, B[i], t) * k)).join(',')})`; };

  // Logo on the cube top. Falls back to the drawn CT mark if the file fails to load.
  const LOGO_SRC = logoSrc;

  const BLUE = '#2f5bff';
  const P_ = 3.0;                 // seconds between inputs
  const PAD_Y = [292, 216, 140];  // station positions along the path
  const PAD_NAMES = ['Discover', 'Design', 'Build'];   // [placeholder: replace with your real process]
  const TOKENS = [
    { name: 'Idea', w: 56 },
    { name: 'Problem', w: 78 },
    { name: 'Opportunity', w: 104 },
  ];

  // prism faces helper
  const faces = (cx, cy, hx, hy, z0, z1) => {
    const x0 = cx - hx, x1 = cx + hx, y0 = cy - hy, y1 = cy + hy;
    return {
      L: PT([iso(x0, y1, z1), iso(x1, y1, z1), iso(x1, y1, z0), iso(x0, y1, z0)]),
      R: PT([iso(x1, y0, z1), iso(x1, y1, z1), iso(x1, y1, z0), iso(x1, y0, z0)]),
      T: PT([iso(x0, y0, z1), iso(x1, y0, z1), iso(x1, y1, z1), iso(x0, y1, z1)]),
    };
  };
  const R = (x, y, w, h, rx, fill, op) =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" fill="${fill}"${op != null ? ` fill-opacity="${op}"` : ''}/>`;

  // ---------- product UIs (drawn flat, then laid onto the iso plane) ----------
  const PRODUCTS = [
    { key: 'Mobile', W: 78, L: 150, ui: () => [
      R(4, 4, 70, 142, 9, '#0f111a'),
      R(29, 8, 20, 4, 2, '#fff', .18),
      R(10, 20, 26, 4, 2, '#fff', .85), R(10, 27, 16, 3, 1.5, '#fff', .3),
      `<circle cx="62" cy="24" r="5" fill="${BLUE}"/>`,
      R(9, 40, 60, 38, 7, 'url(#gBlue)'),
      R(14, 47, 22, 3.5, 1.7, '#fff', .8), R(14, 54, 34, 7, 2.5, '#fff', .95), R(14, 66, 16, 3, 1.5, '#fff', .5),
      ...[0, 1, 2].map(i => {
        const y = 88 + i * 17;
        return `<circle cx="16" cy="${y + 5}" r="5" fill="#fff" fill-opacity=".12"/>` +
          R(26, y + 1, 30, 3.5, 1.7, '#fff', .6) + R(26, y + 8, 20, 3, 1.5, '#fff', .25) + R(52, y + 3, 16, 4, 2, '#fff', .45);
      }),
      R(20, 138, 38, 3, 1.5, '#fff', .25),
    ].join('') },
    { key: 'Web', W: 150, L: 100, ui: () => [
      R(0, 0, 150, 12, 4, '#fff', .06),
      '<circle cx="7" cy="6" r="2" fill="#fff" fill-opacity=".35"/><circle cx="13" cy="6" r="2" fill="#fff" fill-opacity=".35"/><circle cx="19" cy="6" r="2" fill="#fff" fill-opacity=".35"/>',
      R(34, 3.5, 70, 5, 2.5, '#fff', .12),
      R(8, 19, 86, 44, 5, 'url(#gBlue)'),
      R(15, 27, 44, 6, 2, '#fff', .95), R(15, 37, 58, 3.5, 1.7, '#fff', .6), R(15, 43, 40, 3.5, 1.7, '#fff', .4), R(15, 51, 20, 7, 3.5, '#fff'),
      R(100, 19, 42, 20, 4, '#fff', .08), R(104, 24, 20, 3, 1.5, '#fff', .6), R(104, 30, 28, 3, 1.5, '#fff', .25),
      R(100, 43, 42, 20, 4, '#fff', .08), R(104, 48, 20, 3, 1.5, '#fff', .6), R(104, 54, 28, 3, 1.5, '#fff', .25),
      ...[8, 56, 104].map(x => R(x, 69, 38, 24, 4, '#fff', .07) + `<circle cx="${x + 8}" cy="${77}" r="3.5" fill="${BLUE}"/>` + R(x + 8, 85, 22, 3, 1.5, '#fff', .35)),
    ].join('') },
    { key: 'SaaS', W: 120, L: 120, ui: () => [
      R(0, 0, 24, 120, 4, '#fff', .05),
      R(6, 7, 12, 12, 3, BLUE),
      ...[0, 1, 2, 3].map(i => R(7, 30 + i * 13, 10, 5, 2.5, i === 0 ? BLUE : '#fff', i === 0 ? 1 : .3)),
      R(32, 8, 40, 5, 2.5, '#fff', .8), R(92, 7, 22, 7, 3.5, '#fff', .12),
      ...[32, 60, 88].map((x, i) => R(x, 22, 24, 22, 3.5, i === 0 ? BLUE : '#fff', i === 0 ? .9 : .07) + R(x + 4, 27, 12, 2.5, 1.2, '#fff', .45) + R(x + 4, 33, 16, 5, 2, '#fff', .9)),
      R(32, 50, 80, 62, 5, '#fff', .06),
      '<path d="M38 100 L50 92 L62 95 L74 80 L86 74 L98 62 L108 58 L108 106 L38 106 Z" fill="url(#gArea)"/>',
      `<path d="M38 100 L50 92 L62 95 L74 80 L86 74 L98 62 L108 58" fill="none" stroke="${BLUE}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`,
    ].join('') },
    { key: 'AI', W: 100, L: 130, ui: () => [
      `<circle cx="15" cy="15" r="6.5" fill="url(#gBlue)"/>`,
      '<path d="M15 10.5 L16.4 13.6 L19.5 15 L16.4 16.4 L15 19.5 L13.6 16.4 L10.5 15 L13.6 13.6Z" fill="#fff"/>',
      R(26, 10, 30, 4, 2, '#fff', .85), R(26, 17, 18, 3, 1.5, '#fff', .3),
      R(8, 32, 56, 18, 6, '#fff', .09), R(14, 37, 40, 3, 1.5, '#fff', .55), R(14, 43, 26, 3, 1.5, '#fff', .3),
      R(34, 56, 58, 22, 6, BLUE), R(40, 62, 40, 3, 1.5, '#fff', .9), R(40, 69, 28, 3, 1.5, '#fff', .6),
      R(8, 84, 40, 14, 6, '#fff', .09),
      '<circle cx="18" cy="91" r="2" fill="#fff" fill-opacity=".7"/><circle cx="26" cy="91" r="2" fill="#fff" fill-opacity=".5"/><circle cx="34" cy="91" r="2" fill="#fff" fill-opacity=".3"/>',
      R(8, 108, 84, 16, 8, '#fff', .07), R(16, 114, 30, 4, 2, '#fff', .2),
      `<circle cx="82" cy="116" r="5.5" fill="${BLUE}"/>`,
    ].join('') },
  ];

  const TILE_GLYPH = [   // same order as PRODUCTS: Mobile, Web, SaaS, AI
    // Mobile
    '<g fill="none" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round" stroke-linecap="round"><rect x="14" y="8" width="16" height="28" rx="4"/><path d="M20 31h4"/></g>',
    // Web
    '<g fill="none" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round"><rect x="9" y="11" width="26" height="22" rx="3.5"/><path d="M9 18h26"/></g>',
    // SaaS
    '<g fill="currentColor"><rect x="10" y="10" width="10" height="10" rx="2.5"/><rect x="24" y="10" width="10" height="10" rx="2.5"/><rect x="10" y="24" width="10" height="10" rx="2.5"/><rect x="24" y="24" width="10" height="10" rx="2.5"/></g>',
    // AI
    '<path d="M22 8 L26 18 L36 22 L26 26 L22 36 L18 26 L8 22 L18 18Z" fill="currentColor"/>',
  ];

  const TOKEN_GLYPH = [
    // Idea: bulb
    '<circle cx="27" cy="23" r="9"/><path d="M23 35h8M24.5 39h5M27 8v3M14 14l2 2M40 14l-2 2"/>',
    // Problem: tangle
    '<path d="M10 34 C14 12 22 12 22 28 S30 44 34 22 S42 14 44 30"/>',
    // Opportunity: target + arrow
    '<circle cx="27" cy="29" r="14"/><circle cx="27" cy="29" r="6"/><path d="M27 29 L42 12 M34 12 h8 v8"/>',
  ];

  // ---------- build the scene ----------
  let h = '';

  h += `<defs>
    <linearGradient id="gBlue" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#4a74ff"/><stop offset="1" stop-color="#1d3bd0"/></linearGradient>
    <linearGradient id="gLeft" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#4a74ff"/><stop offset="1" stop-color="#1a34b8"/></linearGradient>
    <linearGradient id="gTop" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#262b47"/><stop offset="1" stop-color="#171a2a"/></linearGradient>
    <linearGradient id="gBeam" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stop-color="${BLUE}" stop-opacity=".65"/><stop offset="1" stop-color="${BLUE}" stop-opacity="0"/></linearGradient>
    <linearGradient id="gArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${BLUE}" stop-opacity=".45"/><stop offset="1" stop-color="${BLUE}" stop-opacity="0"/></linearGradient>
    <radialGradient id="gGlow"><stop offset="0" stop-color="${BLUE}" stop-opacity=".6"/><stop offset="1" stop-color="${BLUE}" stop-opacity="0"/></radialGradient>
    <radialGradient id="gFade" cx="${CX}" cy="${CY}" r="310" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#fff"/><stop offset=".55" stop-color="#fff" stop-opacity=".7"/><stop offset="1" stop-color="#000"/></radialGradient>
    <mask id="mGrid"><rect x="-100" y="-100" width="840" height="760" fill="url(#gFade)"/></mask>
  </defs>`;

  // floor grid
  let gd = '';
  for (let k = -8; k <= 8; k++) {
    const a = k * 60, p1 = iso(a, -480), p2 = iso(a, 480), p3 = iso(-480, a), p4 = iso(480, a);
    gd += `M${p1[0].toFixed(1)} ${p1[1].toFixed(1)}L${p2[0].toFixed(1)} ${p2[1].toFixed(1)}M${p3[0].toFixed(1)} ${p3[1].toFixed(1)}L${p4[0].toFixed(1)} ${p4[1].toFixed(1)}`;
  }
  h += `<g mask="url(#mGrid)"><path d="${gd}" stroke="rgba(255,255,255,.07)" stroke-width="1" fill="none"/></g>`;

  // path: soft road + moving dashes
  const road = (y0, y1) => PT([iso(-14, y0), iso(14, y0), iso(14, y1), iso(-14, y1)]);
  h += `<polygon points="${road(430, 66)}" fill="${BLUE}" fill-opacity=".05"/>`;
  h += `<polygon points="${road(-66, -420)}" fill="${BLUE}" fill-opacity=".05"/>`;
  h += `<polyline class="dash" points="${PT([iso(0, 430), iso(0, 66)])}" fill="none" stroke="${BLUE}" stroke-opacity=".6" stroke-width="1.6" stroke-linecap="round" stroke-dasharray="2 7"/>`;
  h += `<polyline class="dash" points="${PT([iso(0, -66), iso(0, -410)])}" fill="none" stroke="${BLUE}" stroke-opacity=".6" stroke-width="1.6" stroke-linecap="round" stroke-dasharray="2 7"/>`;

  // stations: pad + label (beams are drawn later, above tokens)
  PAD_Y.forEach((y, i) => {
    h += `<polygon id="pad${i}" points="${PT([iso(-50, y - 26), iso(50, y - 26), iso(50, y + 26), iso(-50, y + 26)])}" fill="#fff" fill-opacity=".03" stroke="#fff" stroke-opacity=".12"/>`;
    const [px, py] = iso(66, y, 0);
    h += `<text id="sl${i}" x="${(px + 2).toFixed(1)}" y="${(py + 4).toFixed(1)}" text-anchor="start" font-size="11.5" font-weight="600" letter-spacing=".1em" fill="#6b7188">${PAD_NAMES[i].toUpperCase()}</text>`;
  });

  // platform under the cube
  { const f = faces(0, 0, 66, 66, 0, 6);
    h += `<polygon points="${f.R}" fill="#0c0e15" class="edge"/><polygon points="${f.L}" fill="#10121b" class="edge"/><polygon points="${f.T}" fill="#141722" class="edge"/>`; }

  // capability tiles (right-bottom)
  const TILE_X = [132, 192, 252, 312], TILE_Y = -26;
  PRODUCTS.forEach((p, i) => {
    const xc = TILE_X[i], f = faces(xc, TILE_Y, 22, 22, 0, 10);
    h += `<g>
      <polygon points="${f.R}" fill="#0d0f17" class="edge"/><polygon points="${f.L}" fill="#11141d" class="edge"/>
      <polygon id="tt${i}" points="${f.T}" fill="#181b27" class="edge"/>
      <g id="tg${i}" transform="${plane(xc - 22, TILE_Y - 22, 10)}" color="#6b7188">${TILE_GLYPH[i]}</g>
    </g>`;
    const [lx, ly] = iso(xc, TILE_Y, 0);
    h += `<text id="tl${i}" x="${(lx - 4).toFixed(1)}" y="${(ly + 41).toFixed(1)}" text-anchor="middle" font-size="10.5" font-weight="600" letter-spacing=".06em" fill="#6b7188">${p.key.toUpperCase()}</text>`;
  });

  // products (behind the cube; they slide out of its back)
  h += '<g id="products">';
  PRODUCTS.forEach((p, j) => {
    const z = 44, th = 7, x0 = -p.W / 2, y0 = -p.L / 2;
    const fl = PT([iso(x0, y0, 0), iso(-x0, y0, 0), iso(-x0, -y0, 0), iso(x0, -y0, 0)]);
    const sideL = PT([iso(x0, -y0, z), iso(-x0, -y0, z), iso(-x0, -y0, z - th), iso(x0, -y0, z - th)]);
    const sideR = PT([iso(-x0, y0, z), iso(-x0, -y0, z), iso(-x0, -y0, z - th), iso(-x0, y0, z - th)]);
    h += `<g id="pr${j}" style="display:none">
      <polygon points="${fl}" fill="${BLUE}" fill-opacity=".10"/>
      <g id="pf${j}">
        <polygon points="${sideR}" fill="#0f111a" class="edge"/><polygon points="${sideL}" fill="#14172a" class="edge"/>
        <g transform="${plane(x0, y0, z)}">
          <rect x="0" y="0" width="${p.W}" height="${p.L}" rx="4" fill="#1b1f30" stroke="#fff" stroke-opacity=".2" stroke-width="1.2"/>
          ${p.ui()}
        </g>
      </g>
    </g>`;
  });
  h += '</g>';

  // destination square (echoes the blue period in the headline)
  { const [ex, ey] = iso(0, -398, 0);
    h += `<ellipse id="endGlow" cx="${ex.toFixed(1)}" cy="${ey.toFixed(1)}" rx="46" ry="26" fill="url(#gGlow)" opacity="0"/>
          <polygon id="endSq" points="${PT([iso(-9, -407), iso(9, -407), iso(9, -389), iso(-9, -389)])}" fill="${BLUE}" fill-opacity=".4"/>`; }

  // glow on the floor in front of the cube's entry face
  { const [gx, gy] = iso(0, 76, 0);
    h += `<ellipse id="glowE" cx="${gx.toFixed(1)}" cy="${gy.toFixed(1)}" rx="125" ry="62" fill="url(#gGlow)" opacity=".35"/>`; }

  // the CT cube
  { const f = faces(0, 0, 50, 50, 6, 106);
    const portal = PT([iso(-26, 50, 18), iso(26, 50, 18), iso(26, 50, 68), iso(-26, 50, 68)]);
    const topEdge = PT([iso(-50, 50, 106), iso(50, 50, 106), iso(50, -50, 106)]);
    h += `<g>
      <polygon points="${f.R}" fill="#0f111a" class="edge"/>
      <polygon points="${f.L}" fill="url(#gLeft)" class="edge"/>
      <polygon id="cubeHi" points="${f.L}" fill="#fff" fill-opacity="0"/>
      <polygon id="portal" points="${portal}" fill="#cfe0ff" fill-opacity=".12" stroke="#fff" stroke-opacity=".35"/>
      <polygon points="${f.T}" fill="url(#gTop)" class="edge"/>
      <polyline id="topEdge" points="${topEdge}" fill="none" stroke="#fff" stroke-opacity=".3" stroke-width="1.2" stroke-linejoin="round"/>
      <g transform="${plane(-50, -50, 106)}">
        <rect x="8" y="8" width="84" height="84" rx="10" fill="none" stroke="#fff" stroke-opacity=".08"/>
      </g>
      <g transform="${upright(0, 0, 106)}">
        <g id="ctVec">
          <path d="M47 37 A16 16 0 1 0 47 63" fill="none" stroke="#fff" stroke-width="9"/>
          <rect x="64" y="45" width="9" height="21" fill="#fff"/>
          <polygon id="ctBar" points="54,35 87,35 82,45 54,45" fill="${BLUE}"/>
        </g>
        <image id="ctImg" x="12" y="12" width="76" height="76" preserveAspectRatio="xMidYMid meet" style="display:none"/>
      </g>
    </g>`; }

  // beams (scan light as a token passes a station)
  PAD_Y.forEach((y, i) => {
    h += `<polygon id="beam${i}" points="${PT([iso(-50, y, 0), iso(50, y, 0), iso(50, y, 54), iso(-50, y, 54)])}" fill="url(#gBeam)" opacity="0"/>`;
  });

  // tokens + labels
  const tkTpl = (j) => {
    const f = faces(0, 0, 27, 27, 7, 19);
    const sh = PT([iso(-24, -20, 0), iso(30, -20, 0), iso(30, 34, 0), iso(-24, 34, 0)]);
    return `<g id="tk${j}" style="display:none">
      <polygon points="${sh}" fill="#000" fill-opacity=".35"/>
      <polygon id="tkR${j}" points="${f.R}" class="edge"/><polygon id="tkL${j}" points="${f.L}" class="edge"/>
      <polygon id="tkT${j}" points="${f.T}" class="edge"/>
      <g transform="${plane(-27, -27, 19)}" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">${TOKEN_GLYPH[j]}</g>
    </g>`;
  };
  TOKENS.forEach((t, j) => { h += tkTpl(j); });
  TOKENS.forEach((t, j) => {
    const [lx, ly] = iso(0, 0, 19);
    h += `<g id="tp${j}" style="display:none"><g transform="translate(${lx.toFixed(1)} ${(ly - 42).toFixed(1)})">
      <rect x="${-t.w / 2}" y="-12" width="${t.w}" height="24" rx="12" fill="#171923" fill-opacity=".94" stroke="#fff" stroke-opacity=".14"/>
      <circle cx="${-t.w / 2 + 11}" cy="0" r="3" fill="${BLUE}"/>
      <text x="${-t.w / 2 + 19}" y="4.2" font-size="11.5" font-weight="600" fill="#fff" fill-opacity=".92">${t.name}</text>
    </g></g>`;
  });

  svg.innerHTML = h;
  const $ = (id: string) => svg.querySelector('#' + id) as SVGElement;

  // swap in the real logo only if the file actually loads
  if (LOGO_SRC) {
    const probe = new Image();
    probe.onload = () => {
      $('ctImg').setAttribute('href', LOGO_SRC);
      $('ctImg').style.display = '';
      $('ctVec').style.display = 'none';
    };
    probe.src = LOGO_SRC;
  }

  const tk = TOKENS.map((_, j) => ({ g: $('tk' + j), p: $('tp' + j), T: $('tkT' + j), L: $('tkL' + j), R: $('tkR' + j) }));
  const pr = PRODUCTS.map((_, j) => ({ g: $('pr' + j), f: $('pf' + j) }));
  const pads = PAD_Y.map((_, i) => ({ pad: $('pad' + i), beam: $('beam' + i), lbl: $('sl' + i) }));
  const tiles = PRODUCTS.map((_, i) => ({ top: $('tt' + i), glyph: $('tg' + i), lbl: $('tl' + i) }));
  const cube = { hi: $('cubeHi'), portal: $('portal'), edge: $('topEdge'), bar: $('ctBar'), glow: $('glowE') };
  const end = { sq: $('endSq'), glow: $('endGlow') };
  const productLayer = $('products');
  const TK_PIVOT = iso(0, 0, 13);

  // ---------- timeline ----------
  const WP = [[0, 392], [0.85, 292], [1.2, 292], [1.55, 216], [1.9, 216], [2.25, 140], [2.6, 140], [3.0, 58]];
  const tokY = tau => {
    for (let i = 0; i < WP.length - 1; i++) {
      const [t0, y0] = WP[i], [t1, y1] = WP[i + 1];
      if (tau <= t1) return mix(y0, y1, easeIO((tau - t0) / (t1 - t0)));
    }
    return WP[WP.length - 1][1];
  };
  const SPEED = 64, EMIT = 3.0;
  let lastOrder = '';

  function render(t) {
    // cube pulse: fires each time a token reaches the entry face
    let pulse = 0;
    const kp = Math.floor((t - 2.9) / P_);
    for (let k = kp; k >= kp - 1; k--) {
      if (k < 0) continue;
      const a = t - 2.9 - k * P_;
      if (a >= 0) pulse = Math.max(pulse, Math.exp(-a * 2.8));
    }

    // tokens
    const padA = [0, 0, 0];
    const kmax = Math.floor(t / P_);
    for (let j = 0; j < 3; j++) {
      let k = -1;
      for (let q = kmax; q > kmax - 3; q--) if (q >= 0 && q % 3 === j) { k = q; break; }
      const el = tk[j], tau = k >= 0 ? t - k * P_ : -1;
      if (k < 0 || tau < 0 || tau > 3.2) { el.g.style.display = 'none'; el.p.style.display = 'none'; continue; }
      const y = tokY(tau);
      const alpha = smooth(tau / 0.45) * (1 - smooth((tau - 2.7) / 0.4));
      const sc = 1 - 0.45 * smooth((tau - 2.7) / 0.45);
      const dx = -y * C * S, dy = y * 0.5 * S;
      const prog = smooth((292 - y) / 152);
      const col = mixC('#252a3d', BLUE, prog);
      el.g.style.display = ''; el.p.style.display = '';
      el.g.setAttribute('transform', `translate(${dx.toFixed(1)} ${dy.toFixed(1)}) translate(${TK_PIVOT[0].toFixed(1)} ${TK_PIVOT[1].toFixed(1)}) scale(${sc.toFixed(3)}) translate(${-TK_PIVOT[0].toFixed(1)} ${-TK_PIVOT[1].toFixed(1)})`);
      el.g.setAttribute('opacity', alpha.toFixed(3));
      el.T.setAttribute('fill', col);
      el.L.setAttribute('fill', mixC('#252a3d', BLUE, prog, .55));
      el.R.setAttribute('fill', mixC('#252a3d', BLUE, prog, .38));
      el.p.setAttribute('transform', `translate(${dx.toFixed(1)} ${dy.toFixed(1)})`);
      el.p.setAttribute('opacity', (alpha * (1 - smooth((tau - 2.45) / 0.25))).toFixed(3));
      PAD_Y.forEach((py, i) => { padA[i] = Math.max(padA[i], clamp(1 - Math.abs(y - py) / 46) * alpha); });
    }
    pads.forEach((p, i) => {
      const a = padA[i];
      p.pad.setAttribute('fill', a > 0.01 ? BLUE : '#fff');
      p.pad.setAttribute('fill-opacity', (0.03 + 0.25 * a).toFixed(3));
      p.pad.setAttribute('stroke', a > 0.01 ? BLUE : '#fff');
      p.pad.setAttribute('stroke-opacity', (0.12 + 0.7 * a).toFixed(3));
      p.beam.setAttribute('opacity', (a * 0.9).toFixed(3));
      p.lbl.setAttribute('fill', mixC('#6b7188', '#ffffff', a));
    });

    // products
    const kE = Math.floor((t - EMIT) / P_);
    const vis = [];
    const act = [0, 0, 0, 0];
    let arrive = 0;
    for (let j = 0; j < 4; j++) {
      let k = -1;
      for (let q = kE; q > kE - 4; q--) if (q >= 0 && q % 4 === j) { k = q; break; }
      const el = pr[j], tp = k >= 0 ? t - EMIT - k * P_ : -1;
      if (k < 0 || tp < 0 || tp > 5.0) { el.g.style.display = 'none'; continue; }
      const yStart = -PRODUCTS[j].L / 2 - 45;          // starts just behind the cube's back face
      const y = yStart - SPEED * tp;
      const tA = (385 + yStart) / SPEED;                // arrival time at the end square
      const alpha = smooth(tp / 0.4) * (1 - smooth((tp - (tA - 0.7)) / 0.75));
      const bob = 2.2 * Math.sin(t * 1.6 + j * 1.7);
      el.g.style.display = '';
      el.g.setAttribute('transform', `translate(${(-y * C * S).toFixed(1)} ${(y * 0.5 * S).toFixed(1)})`);
      el.g.setAttribute('opacity', alpha.toFixed(3));
      el.f.setAttribute('transform', `translate(0 ${(-bob * S).toFixed(2)})`);
      act[j] = smooth(tp / 0.4) * (1 - smooth((tp - 2.8) / 0.8));
      arrive = Math.max(arrive, Math.exp(-Math.abs(tp - tA) * 3));
      vis.push([k, j]);
    }
    vis.sort((a, b) => a[0] - b[0]);
    const order = vis.map(v => v[1]).join('');
    if (order !== lastOrder) { vis.forEach(v => productLayer.appendChild(pr[v[1]].g)); lastOrder = order; }

    tiles.forEach((tl, i) => {
      const a = act[i];
      tl.top.setAttribute('fill', mixC('#181b27', BLUE, a));
      tl.glyph.setAttribute('color', mixC('#6b7188', '#ffffff', a));
      tl.lbl.setAttribute('fill', mixC('#6b7188', '#ffffff', a));
    });

    end.sq.setAttribute('fill-opacity', (0.35 + 0.65 * arrive).toFixed(3));
    end.glow.setAttribute('opacity', (arrive * 0.9).toFixed(3));

    // cube
    cube.hi.setAttribute('fill-opacity', (pulse * 0.2).toFixed(3));
    cube.portal.setAttribute('fill-opacity', (0.1 + 0.6 * pulse).toFixed(3));
    cube.edge.setAttribute('stroke', mixC('#ffffff', '#8fb0ff', pulse));
    cube.edge.setAttribute('stroke-opacity', (0.3 + 0.6 * pulse).toFixed(3));
    cube.glow.setAttribute('opacity', (0.35 + 0.65 * pulse).toFixed(3));
  }

  // ---------- clock ----------
  let clock = 9.6;
  const reduce = reducedMotion || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduce) {
    render(clock);
    return () => {};
  }

  let last = performance.now();
  let visible = true;
  let frame = 0;
  const observer = new IntersectionObserver((entries) => {
    visible = entries[0].isIntersecting;
  });
  observer.observe(svg);
  const loop = (now: number) => {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    if (visible) {
      clock += dt;
      render(clock);
    }
    frame = requestAnimationFrame(loop);
  };
  frame = requestAnimationFrame(loop);
  return () => {
    cancelAnimationFrame(frame);
    observer.disconnect();
  };
}
